import { Injectable, inject } from '@angular/core';
import { SecureStorageService } from './secure-storage.service';

/**
 * Key used for storing CSRF token in secure storage
 */
const CSRF_TOKEN_KEY: string = 'csrfToken';

/**
 * Length of the CSRF token in bytes (results in 64 hex characters)
 */
const TOKEN_LENGTH: number = 32;

/**
 * Bytes per hex character representation
 */
const HEX_PAD_LENGTH: number = 2;

/**
 * Radix for hex encoding
 */
const HEX_RADIX: number = 16;

/**
 * CSRF Protection Service
 * 
 * Implements double-submit cookie pattern for CSRF protection:
 * 1. Generates a cryptographically secure random token
 * 2. Token is sent in both a header and stored securely
 * 3. Server should validate that the header token matches the expected value
 * 
 * For full CSRF protection, the server must:
 * - Generate and set an httpOnly CSRF cookie on responses
 * - Validate the X-CSRF-Token header matches the cookie value
 */
@Injectable({
  providedIn: 'root'
})
export class CsrfService {
  private readonly secureStorage: SecureStorageService = inject(SecureStorageService);

  /**
   * Generate a cryptographically secure CSRF token
   * Uses Web Crypto API for secure random generation
   */
  generateToken(): string {
    const array: Uint8Array = new Uint8Array(TOKEN_LENGTH);
    crypto.getRandomValues(array);
    const token: string = Array.from(array, (byte: number) => byte.toString(HEX_RADIX).padStart(HEX_PAD_LENGTH, '0')).join('');
    this.secureStorage.setItem(CSRF_TOKEN_KEY, token);
    return token;
  }

  /**
   * Get the current CSRF token, generating one if it doesn't exist
   */
  getToken(): string {
    let token: string | null = this.secureStorage.getItem(CSRF_TOKEN_KEY);
    if (!token) {
      token = this.generateToken();
    }
    return token;
  }

  /**
   * Regenerate the CSRF token (call after successful sensitive operations)
   */
  refreshToken(): string {
    return this.generateToken();
  }

  /**
   * Clear the CSRF token (call on logout)
   */
  clearToken(): void {
    this.secureStorage.removeItem(CSRF_TOKEN_KEY);
  }

  /**
   * Get the header name for CSRF token
   */
  getHeaderName(): string {
    return 'X-CSRF-Token';
  }
}
